import { Alert, Button, Switch, TextInput } from '@mantine/core'
import { AdminScopeTestBidder, isIpOrCidrPrefixTooWide, isValidIpOrCidr, PREFIX_TOO_WIDE_MESSAGE } from '@pubstack/common/src/pbs/pbs-test-bidder'
import { FunctionComponent, useState } from 'react'

type Props = {
  /** Current server state; `undefined` while loading. */
  testBidder: AdminScopeTestBidder | undefined
  /** Whether the scope has a proxy feature; `scope: true` can only be turned on when it does. */
  isAvailable: boolean
  isSaving?: boolean
  /** A rejected promise keeps the draft so the user can retry; a resolved one resets it to the refreshed server state. */
  onSave: (update: { scope: boolean; allowedIps: string[] }) => void | Promise<unknown>
}

export const EMPTY_ALLOWLIST_WARNING = 'No allowed IP: the test bidder will answer nobody.'

export const PureAdminScopeTestBidder: FunctionComponent<Props> = ({ testBidder, isAvailable, isSaving = false, onSave }) => {
  // The user's edits; `null` until something is touched, so the server state is shown (and refreshed) in the meantime
  const [draft, setDraft] = useState<{ scope: boolean; rows: string[] } | null>(null)
  const scope = draft?.scope ?? testBidder?.scope ?? false
  const rows = draft?.rows ?? testBidder?.allowedIps ?? []
  const setScope = (value: boolean) => setDraft({ scope: value, rows })
  const setRows = (update: (current: string[]) => string[]) => setDraft({ scope, rows: update(rows) })

  const entries = rows.map((row) => row.trim()).filter((row) => row !== '')
  const errors = rows.map((row) => {
    const value = row.trim()
    if (value === '' || isValidIpOrCidr(value)) return undefined
    return isIpOrCidrPrefixTooWide(value) ? PREFIX_TOO_WIDE_MESSAGE : 'Not a valid IPv4/IPv6 address or CIDR'
  })
  const hasError = errors.some((error) => error !== undefined)
  const save = async () => {
    try {
      await onSave({ scope, allowedIps: entries })
    } catch {
      return // The mutation toasts the error; keep the draft for a retry
    }
    setDraft(null)
  }
  const scopeSwitchDisabled = !testBidder || (!isAvailable && !scope)

  return (
    <section className="flex flex-col gap-4" aria-label="Server-side test bidder">
      <h2 className="font-rubik text-base leading-[26px] font-medium">Server-side test bidder</h2>
      <Switch label="Enabled on all sites" checked={scope} disabled={scopeSwitchDisabled} onChange={(event) => setScope(event.currentTarget.checked)} />
      {!isAvailable && <div className="text-sm text-slate-grey">Not available: this scope has no Prebid Server proxy feature.</div>}

      <div>
        <div className="font-medium">Individually enabled sites</div>
        {testBidder && testBidder.sites.length > 0 ? (
          <ul aria-label="Individually enabled sites" className="list-inside list-disc text-sm">
            {testBidder.sites.map((siteId) => (
              <li key={siteId}>{siteId}</li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-slate-grey">None. Sites are enabled from their setup flyout.</div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="font-medium">Allowed IPs</div>
        <div className="text-sm text-slate-grey">IPv4 or IPv6 addresses, or CIDR ranges. The test bidder only answers requests coming from these IPs.</div>
        {rows.map((row, index) => (
          <div key={index} className="flex items-start gap-2">
            <TextInput
              className="w-80"
              aria-label={`Allowed IP ${index + 1}`}
              value={row}
              error={errors[index]}
              onChange={(event) => {
                const value = event.currentTarget.value
                setRows((current) => current.map((r, i) => (i === index ? value : r)))
              }}
            />
            <Button variant="default" aria-label={`Remove allowed IP ${index + 1}`} onClick={() => setRows((current) => current.filter((_, i) => i !== index))}>
              Remove
            </Button>
          </div>
        ))}
        <div>
          <Button variant="default" onClick={() => setRows((current) => [...current, ''])}>
            Add an IP
          </Button>
        </div>
        {entries.length === 0 && (
          <Alert color="yellow" role="alert">
            {EMPTY_ALLOWLIST_WARNING}
          </Alert>
        )}
      </div>

      <div>
        <Button disabled={!testBidder || hasError || isSaving} loading={isSaving} onClick={save}>
          Save
        </Button>
      </div>
    </section>
  )
}
